import {
  AdmCompany,
  AdmMember,
  AdmProject,
  AllMemberTypes,
} from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  AllAddons,
  AllAddonsTypes,
  AllBundles,
  AllBundlesTypes,
  AllCustomProducts,
  AllCustomProductsTypes,
  AllSphereXgProductsAllYears,
  AllSphereXg2026Products,
  AllFeatures,
  AllFeaturesTypes,
  AllSubscriptions,
  AllSubscriptionsTypes,
  AllTrialProducts,
  AllTrialProductTypes,
  AllCreditProducts,
  AllEnterpriseProducts,
  AllHoloBuilderLegacyPlans,
} from "@utils/product/product-types";
import {
  ProductIdentifier,
  ProductMap,
  ProductType,
} from "@utils/product/product-utils-types";

/**
 * Whether the context is a company context
 * TODO: With the backend changes, every context always includes a defined 'Company' object. Check if this is needed anymore.
 */
export function isCompanyContext(
  context:
    | FaroCoreApiTypes.AdmProjectCompanyContext
    | FaroCoreApiTypes.AdmProjectUserContext
): context is FaroCoreApiTypes.AdmProjectCompanyContext {
  return context.kind === "COMPANY";
}

/** Whether the product is a feature that is allowed in admin panel or not */
export function isProductAllowedFeature(
  feature: ProductIdentifier
): feature is AllFeaturesTypes {
  return (AllFeatures as readonly string[]).includes(feature);
}

/** Whether the product is a bundle that is allowed in admin panel or not  */
export function isProductAllowedBundle(
  bundle: ProductIdentifier
): bundle is AllBundlesTypes {
  return (AllBundles as readonly string[]).includes(bundle);
}

/** Whether the product is a subscription that is allowed in admin panel or not  */
export function isProductAllowedSubscription(
  subscription: ProductIdentifier
): subscription is AllSubscriptionsTypes {
  return (AllSubscriptions as readonly string[]).includes(subscription);
}

/** Whether the product is an addon that is allowed in admin panel or not  */
export function isProductAllowedAddon(
  addon: ProductIdentifier
): addon is AllAddonsTypes {
  return (AllAddons as readonly string[]).includes(addon);
}

/** Whether the product is a custom product that is allowed in admin panel or not  */
export function isProductAllowedCustom(
  custom: ProductIdentifier
): custom is AllCustomProductsTypes {
  return (AllCustomProducts as readonly string[]).includes(custom);
}

/** Whether the product is a trial product that is allowed in admin panel or not  */
export function isProductAllowedTrial(
  custom: ProductIdentifier
): custom is AllTrialProductTypes {
  return (AllTrialProducts as readonly string[]).includes(custom);
}

/** Whether the product is a Sphere Xg custom product identifier that is allowed in admin panel or not  */
export function isSphereXgProductIdentifier<T extends ProductType>(
  productIdentifier?: ProductMap<T>
): productIdentifier is ProductMap<T> {
  if (!productIdentifier) {
    return false;
  }
  return (AllSphereXgProductsAllYears as readonly string[]).includes(
    productIdentifier
  );
}

/** Whether the product is a Sphere Xg 2026 custom product identifier that is allowed in admin panel or not  */
export function is2026SphereXgProductIdentifier<T extends ProductType>(
  productIdentifier?: ProductMap<T>
): productIdentifier is ProductMap<T> {
  if (!productIdentifier) {
    return false;
  }
  return (AllSphereXg2026Products as readonly string[]).includes(
    productIdentifier
  );
}

/** Whether the product is one of the legacy HoloBuilder plans that are modelled as bundles */
export function isHoloBuilderLegacyPlanIdentifier<T extends ProductType>(
  productIdentifier?: ProductMap<T>
): productIdentifier is ProductMap<T> {
  if (!productIdentifier) {
    return false;
  }

  return (AllHoloBuilderLegacyPlans as readonly string[]).includes(
    productIdentifier
  );
}

/** Whether the product is a credit product identifier that is allowed in admin panel or not  */
export function isCreditProductIdentifier<T extends ProductType>(
  productIdentifier?: ProductMap<T>
): productIdentifier is ProductMap<T> {
  if (!productIdentifier) {
    return false;
  }

  return (AllCreditProducts as readonly string[]).includes(productIdentifier);
}

/** Whether the product is an enterprise product identifier that is allowed in admin panel or not  */
export function isEnterpriseProductIdentifier<T extends ProductType>(
  productIdentifier?: ProductMap<T>
): productIdentifier is ProductMap<T> {
  if (!productIdentifier) {
    return false;
  }

  return (AllEnterpriseProducts as readonly string[]).includes(
    productIdentifier
  );
}

/** Whether the project type is in the AdmProject type */
export function isAdmProject(
  project: AdmProject | FaroCoreApiTypes.AdmMembersProjects
): project is AdmProject {
  return (
    "id" in project &&
    "accessLevel" in project &&
    "address" in project &&
    "area" in project &&
    "context" in project &&
    "state" in project
  );
}

/** Whether the member type is in the AdmMember type */
export function isAdmMember(member: AllMemberTypes): member is AdmMember {
  return "emailVerified" in member && "companies" in member;
}

/** Whether the company type is in the AdmUserCompany type */
export function isAdmUserCompany(
  company: AdmCompany | FaroCoreApiTypes.AdmUserCompany
): company is FaroCoreApiTypes.AdmUserCompany {
  return "role" in company;
}
